import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ChampionshipManagerService from '../../services/championshipManagerService';
import useAuth from '../../hooks/useAuth';
import './ChampionshipManagers.css';

function ChampionshipManagers({ championship }) {
  const params = useParams();
  const championshipId = championship?._id || params.id || params.championshipId;
  const { user } = useAuth();

  const [managers, setManagers] = useState([]);
  const [creator, setCreator] = useState(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [emailError, setEmailError] = useState(null);

  const validateEmail = useCallback(
    (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
    []
  );

  // 🔹 Permissão: apenas Admin, criador ou gestores do campeonato
  const canManageManagers = useMemo(() => {
    if (!user) return false;
    // Admin sempre pode gerenciar
    if (user.role.toLowerCase() === 'admin') return true;

    const userId = user._id;
    const isCreator = creator?._id === userId;
    const isManager = managers?.some((m) => m._id === userId);
    return isCreator || isManager;
  }, [user, creator, managers]);

  const loadManagers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // O endpoint getManagers no backend já possui proteção, então a chamada aqui
      // depende da autenticação e role do usuário. Se o usuário não tiver permissão
      // no backend (e não for Admin), ele não conseguirá carregar a lista.
      const result = await ChampionshipManagerService.getManagers(championshipId);

      if (result.success) {
        setManagers(result.managers || []);
        setCreator(result.creator || null);
      } else {
        // Se o erro for 403, pode ser que o usuário não seja gestor/admin do campeonato,
        // mas o frontend está tentando carregar para exibição restrita.
        // Neste caso, ainda precisamos saber quem é o criador do campeonato
        // para a lógica `canManageManagers` funcionar corretamente.
        // Isso pode ser obtido via `championshipService.getById(championshipId)`
        // que é a chamada de nível superior no ChampionshipDetail.jsx.
        setError(result.error || 'Erro ao carregar gestores');
      }
    } catch (err) {
      // Se a API retornar 403 ou outro erro de rede
      console.error("Erro ao carregar gestores:", err);
      setError('Falha na conexão com o servidor ou você não tem permissão para ver os gestores.');
    } finally {
      setLoading(false);
    }
  }, [championshipId]);

  useEffect(() => {
    if (!championshipId) {
      setError('ID do campeonato não encontrado');
      return;
    }
    loadManagers();
  }, [championshipId, loadManagers]);

  const handleAddManager = async (e) => {
    e.preventDefault();
    setError(null);
    setEmailError(null);

    if (!email.trim()) return setEmailError('O email do novo gestor é obrigatório');
    if (!validateEmail(email.trim())) return setEmailError('Por favor, insira um email válido');
    
    // Verificação de permissão no frontend antes de enviar a requisição
    if (!canManageManagers) {
      setError('Você não tem permissão para adicionar gestores neste campeonato.');
      return;
    }

    try {
      setLoading(true);
      setSuccess(null);
      const result = await ChampionshipManagerService.addManager(championshipId, email.trim());
      if (result.success) {
        setEmail('');
        setManagers(result.data?.managers || []);
        setCreator(result.data?.creator || null); // Atualiza o criador também, caso tenha sido populado
        setSuccess('Gestor adicionado com sucesso!');
      } else {
        setError(result.error || 'Erro ao adicionar gestor');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao adicionar gestor');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveManager = async (managerId) => {
    // Verificação de permissão no frontend antes de enviar a requisição
    if (!canManageManagers) {
      setError('Você não tem permissão para remover gestores neste campeonato.');
      return;
    }
    if (!managerId) {
      setError('ID do gestor inválido para remoção.');
      return;
    }
    
    try {
      setLoading(true);
      setSuccess(null);
      const result = await ChampionshipManagerService.removeManager(championshipId, managerId);
      if (result.success) {
        setManagers(result.data?.managers || []);
        setCreator(result.data?.creator || null); // Atualiza o criador
        setSuccess('Gestor removido com sucesso!');
      } else {
        setError(result.error || 'Erro ao remover gestor');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao remover gestor');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{2})(\d{5})(\d{4})$/);
    if (match) return `(${match[1]}) ${match[2]}-${match[3]}`;
    const matchAlt = cleaned.match(/^(\d{2})(\d{4})(\d{4})$/);
    if (matchAlt) return `(${matchAlt[1]}) ${matchAlt[2]}-${matchAlt[3]}`;
    return phoneNumber;
  };

  const generateWhatsAppLink = (phoneNumber) => {
    if (!phoneNumber) return '';
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    return `https://wa.me/55${cleaned}`;
  };

  if (!championshipId) {
    return (
      <motion.div className="error-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <h3>Erro: Campeonato não identificado</h3>
        <p>O ID do campeonato não foi encontrado na URL.</p>
      </motion.div>
    );
  }

  return (
    <motion.div className="managers-container" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <h2>Gestores do Campeonato</h2>

      {loading && (
        <motion.div className="loading-indicator" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          ⏳ Carregando...
        </motion.div>
      )}

      {/* Formulário de adicionar gestores - RENDERIZA SOMENTE SE O USUÁRIO PODE GERENCIAR */}
      {canManageManagers && (
        <motion.form onSubmit={handleAddManager} className="manager-form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="input-group">
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError(null);
              }}
              placeholder="Email do novo gestor"
              required
              disabled={loading}
              className={emailError ? 'input-error' : ''}
            />
            <button type="submit" disabled={loading} className="add-button">
              {loading ? 'Adicionando...' : 'Adicionar'}
            </button>
          </div>
          {emailError && <div className="error-text">{emailError}</div>}
        </motion.form>
      )}

      {/* Mensagens */}
      <AnimatePresence>
        {error && (
          <motion.div className="error-message" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            {error}
            <button onClick={() => setError(null)} className="close-button">×</button>
          </motion.div>
        )}
        {success && (
          <motion.div className="success-message" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            {success}
            <button onClick={() => setSuccess(null)} className="close-button">×</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Criador */}
      {creator && (
        <motion.div className="creator-info-container" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <h3>Criador do Campeonato</h3>
          <div className="manager-item">
            {creator.avatar && <img src={creator.avatar} alt={creator.name} className="manager-avatar" />}
            <div className="manager-info">
              <span className="manager-name">{creator.name} (Criador)</span>
              <span className="manager-email">📧 {creator.email}</span>
              <span className="manager-phone">
                {creator.phone ? (
                  <a href={generateWhatsAppLink(creator.phone)} target="_blank" rel="noopener noreferrer" className="manager-phone-link">
                    📱 {formatPhoneNumber(creator.phone)}
                  </a>
                ) : '📱 Telefone não informado'}
              </span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Lista de gestores */}
      <div className="managers-list">
        <h3>Gestores Atuais</h3>
        {managers.length === 0 ? (
          <p>Nenhum gestor adicional cadastrado</p>
        ) : (
          <ul>
            <AnimatePresence>
              {managers.map((manager) => (
                <motion.li key={manager._id} className="manager-item" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.2 }}>
                  <div className="manager-info">
                    {manager.avatar && <img src={manager.avatar} alt={manager.name} className="manager-avatar" />}
                    <span className="manager-name">{manager.name}</span>
                    <span className="manager-email">📧 {manager.email}</span>
                    <span className="manager-phone">
                      {manager.phone ? (
                        <a href={generateWhatsAppLink(manager.phone)} target="_blank" rel="noopener noreferrer" className="manager-phone-link">
                          📱 {formatPhoneNumber(manager.phone)}
                        </a>
                      ) : '📱 Telefone não informado'}
                    </span>
                  </div>
                  {/* Botão de remover gestor - RENDERIZA SOMENTE SE O USUÁRIO PODE GERENCIAR */}
                  {canManageManagers && (
                    <button onClick={() => handleRemoveManager(manager._id)} disabled={loading} className="remove-button">
                      Remover
                    </button>
                  )}
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>
    </motion.div>
  );
}

export default ChampionshipManagers;